import { faHome } from '@fortawesome/pro-light-svg-icons'

import { DashboardModule } from '@domain-group/suit-theme'

import useCampaignStatuses from '@hooks/useCampaignStatuses'
import useCampaigns from '@hooks/useCampaigns'
import useCurrentUser from '@hooks/useCurrentUser'

import type { UserModel } from '@models/types'
import { CampaignRequestOptions } from '@models/campaign'

import { sanitizeFilters } from '@functions/entity'

import ItemContainer from './ItemContainer'

export const icon = faHome

type ConfigOptionsParams = {
  currentUser?: UserModel,
  isAgent?: boolean,
}

export const configOptions = (params: ConfigOptionsParams) => {
  const { currentUser, isAgent } = params

  const options = {
    title: {
      component: 'input',
      key: 'title',
      label: 'Title',
      optionKey: 'customization',
    },
    campaign_status: {
      component: 'select',
      key: 'campaign_status',
      label: 'Campaign Status',
      optionKey: 'queryParams',
      values: [
        { key: 'all', label: 'All' },
        { key: 'current', label: 'Current' },
        { key: 'leased', label: 'Leased' },
        { key: 'offmarket', label: 'Off Market' },
        { key: 'proposal', label: 'Proposal' },
        { key: 'sold', label: 'Sold' },
        { key: 'withdrawn', label: 'Withdrawn' },
      ],
    },
    published: {
      component: 'select',
      key: 'published',
      label: 'Published',
      optionKey: 'queryParams',
      values: [
        { key: 'true', label: 'Published' },
        { key: 'false', label: 'Not Published' },
      ],
    },
    priority_status: {
      component: 'select',
      hidden: isAgent,
      key: 'priority_campaign_user_id',
      label: 'Priority Status',
      optionKey: 'queryParams',
      values: [
        { key: 'all', label: 'All' },
        { key: currentUser?.id, label: 'Priority Only' },
      ],
    },
    display_mode: {
      component: 'select',
      key: 'display_mode',
      label: 'Display Mode',
      optionKey: 'customization',
      values: [
        { key: 'compact-list', label: 'List (Compact)' },
        { key: 'list', label: 'List (Large)' },
        { key: 'tiles', label: 'Tiles' },
      ],
    },
    limit: {
      component: 'select',
      key: 'limit',
      label: 'Limit',
      optionKey: 'queryParams',
      values: [
        { key: 5, label: '5' },
        { key: 10, label: '10' },
        { key: 25, label: '25' },
        { key: 50, label: '50' },
      ],
    },
  }
  return options
}

interface CampaignsProps {
  customOptions: {
    queryParams: {
      key?: string,
      limit?: number,
    },
    customization: {
      display_mode: 'compact-list' | 'list' | 'tiles',
      title?: string,
    },
    requestOptions: CampaignRequestOptions,
  },
  editing: boolean,
}

const Campaigns = (props: CampaignsProps) => {
  const {
    customOptions: {
      customization,
      requestOptions,
      queryParams,
    },
    editing,
  } = props

  const { display_mode, title } = customization || {}
  const filters = sanitizeFilters(queryParams)

  const { currentUser, isAgent } = useCurrentUser()

  useCampaignStatuses({ performHttpRequests: true })

  const campaignsPayload = useCampaigns({
    filters: {
      agency_marketing: 'false',
      hidden: 'false',
      ...filters,
    },
    performHttpRequests: true,
    requestOptions: {
      include_campaign_image: true,
      include_campaign_priority_campaign_user_ids: !isAgent,
      ...requestOptions,
    },
  })

  const {
    filteredCampaigns,
    hasFilteredCampaigns,
    loading,
  } = campaignsPayload

  const campaignsArray = filteredCampaigns.slice(0, queryParams.limit || 50)

  return (
    <DashboardModule
      boxProps={{ marginBottom: 0 }}
      contentBoxProps={{ flexDirection: 'column' }}
      editing={editing}
      loading={loading}
      title={title || 'Campaigns'}
    >
      {!loading && !hasFilteredCampaigns && (
        <DashboardModule.BlankState>
          <DashboardModule.BlankState.Title>
            No Campaigns to Display
          </DashboardModule.BlankState.Title>

          <DashboardModule.BlankState.Paragraph>
            You either have no campaigns or the filters defined returned no results. Create a new campaign or change
            your filters.
          </DashboardModule.BlankState.Paragraph>
        </DashboardModule.BlankState>
      )}

      {hasFilteredCampaigns && (
        // @ts-expect-error TS2322
        <DashboardModule.ScrollableSection maxHeight={800}>
          <ItemContainer
            campaigns={campaignsArray}
            // @ts-expect-error TS2322
            currentUser={currentUser}
            displayMode={display_mode}
          />
        </DashboardModule.ScrollableSection>
      )}
    </DashboardModule>
  )
}

export default Campaigns
